/**
 * Event leaderboard landing — the main public live dashboard for an event.
 *
 * Title bar carries the event name, live status, and last-updated time. The
 * team standings are the focus: a gamified board with gold/silver/bronze top
 * three, station progress, large type, real-time score highlights, and a
 * position-change (FLIP) animation when teams move. Individual standings link
 * sits at the bottom.
 *
 * Requirements: 16.1 (public leaderboard), 16.2 (team default), 16.3 (real-time)
 */
import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import type { TeamLeaderboardEntry } from '@scoring/shared';
import { usePublicApiClient } from '../hooks/usePublicApiClient.js';
import { useLeaderboardSocket } from '../hooks/useLeaderboardSocket.js';
import type { LeaderboardUpdateMessage } from '../hooks/useLeaderboardSocket.js';
import { EventStatusBadge } from '../components/EventStatusBadge.js';
import { LeaderboardHeader } from '../components/LeaderboardHeader.js';
import { DonationButton } from '@scoring/frontend-shared';

interface PublicEvent {
  id: string;
  name: string;
  status: string;
}

const MEDALS = ['🥇', '🥈', '🥉'];

export function LeaderboardPage(): JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const apiClient = usePublicApiClient();

  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [entries, setEntries] = useState<TeamLeaderboardEntry[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [updatedTeamIds, setUpdatedTeamIds] = useState<Set<string>>(new Set());
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // FLIP: animate rows sliding to their new position when the order changes.
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const prevTops = useRef<Map<string, number>>(new Map());

  useLayoutEffect(() => {
    const newTops = new Map<string, number>();
    rowRefs.current.forEach((el, id) => {
      const top = el.getBoundingClientRect().top;
      newTops.set(id, top);
      const prev = prevTops.current.get(id);
      if (prev !== undefined && prev !== top) {
        el.style.transition = 'none';
        el.style.transform = `translateY(${prev - top}px)`;
        requestAnimationFrame(() => {
          el.style.transition = 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)';
          el.style.transform = '';
        });
      }
    });
    prevTops.current = newTops;
  }, [entries]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;

    async function fetchData(): Promise<void> {
      try {
        setLoading(true);
        setError(null);
        const [board, eventsResp] = await Promise.all([
          apiClient.request<{ entries: TeamLeaderboardEntry[]; lastUpdated: string }>(
            'GET',
            `/public/leaderboard/${encodeURIComponent(eventId!)}/team`,
            { public: true },
          ),
          apiClient.request<{ events: PublicEvent[] }>('GET', '/public/events', { public: true }),
        ]);
        if (cancelled) return;
        setEntries(board.entries);
        setLastUpdated(board.lastUpdated);
        setEvent(eventsResp.events.find((e) => e.id === eventId) ?? null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchData();
    return () => {
      cancelled = true;
    };
  }, [eventId, apiClient]);

  const handleUpdate = useCallback(
    (message: LeaderboardUpdateMessage) => {
      if (message.eventId !== eventId) return;
      const previousScores = new Map(entries.map((e) => [e.teamId, e.totalScore]));
      const changedIds = new Set<string>();
      for (const entry of message.team) {
        const prev = previousScores.get(entry.teamId);
        if (prev === undefined || prev !== entry.totalScore) changedIds.add(entry.teamId);
      }
      setEntries(message.team);
      setLastUpdated(message.lastUpdated);
      if (changedIds.size > 0) {
        setUpdatedTeamIds(changedIds);
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => setUpdatedTeamIds(new Set()), 2000);
      }
    },
    [eventId, entries],
  );

  useLeaderboardSocket({ eventId, onUpdate: handleUpdate, enabled: !loading && !error });

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  return (
    <div className="leaderboard-container">
      <LeaderboardHeader
        eventName={event?.name ?? 'Event Leaderboard'}
        lastUpdated={lastUpdated}
        statusNode={event ? <EventStatusBadge status={event.status} /> : undefined}
      />

      {loading && <p className="loading-message">Loading team standings…</p>}
      {error && <p className="error-message" role="alert">{error}</p>}

      {!loading && !error && (
        entries.length === 0 ? (
          <p className="empty-message">No team scores recorded yet.</p>
        ) : (
          <table className="leaderboard-table gamified" aria-label="Team standings">
            <tbody>
              {entries.map((entry) => {
                const rankClass = entry.rank <= 3 ? `rank-${entry.rank}` : '';
                const updated = updatedTeamIds.has(entry.teamId) ? ' updated' : '';
                return (
                  <tr
                    key={entry.teamId}
                    ref={(el) => {
                      if (el) rowRefs.current.set(entry.teamId, el);
                      else rowRefs.current.delete(entry.teamId);
                    }}
                    className={`leaderboard-row ${rankClass}${updated}`}
                  >
                    <td className="rank-cell">
                      <span className="rank-badge">{MEDALS[entry.rank - 1] ?? entry.rank}</span>
                    </td>
                    <td className="name-cell">
                      <span className="team-name">{entry.teamName}</span>
                      {entry.totalStations > 0 && (
                        <span className="stations-progress" aria-label={`${entry.stationsCompleted} of ${entry.totalStations} stations completed`}>
                          🎯 {entry.stationsCompleted}/{entry.totalStations} stations
                        </span>
                      )}
                    </td>
                    <td className="score-cell">
                      {entry.totalScore}
                      <span className="score-label">pts</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}

      <nav className="leaderboard-nav" aria-label="Leaderboard views">
        <Link to={`/${eventId}/individual`} className="nav-link">
          View Individual Standings →
        </Link>
      </nav>

      <footer className="leaderboard-footer">
        <DonationButton />
      </footer>
    </div>
  );
}
